import { z } from "zod"
import { SupportedRegions, projectEntityMap } from "@nova/constants"
import { EventKind } from "./event-kind"

type EntityMap = typeof projectEntityMap
type KeysOfKind<Kind> = {
    [K in keyof EntityMap]: EntityMap[K]["kind"] extends Kind ? K : never
}[keyof EntityMap]

const layerTypeKeys = <Kind extends "layer" | "entity">(kind: Kind) =>
    (Object.keys(projectEntityMap) as (keyof EntityMap)[]).filter(
        (k) => projectEntityMap[k].kind === kind
    ) as [KeysOfKind<Kind>, ...KeysOfKind<Kind>[]]

const layer = z.object({
    layerId: z.string(),
    projectId: z.string(),
    layerType: z.enum(layerTypeKeys("layer")),
})
const entity = z.object({
    entityId: z.string(),
    projectId: z.string(),
    entityType: z.enum(layerTypeKeys("entity")),
})
const stream = z.object({ layerId: z.string(), projectId: z.string() })
const pdf = z.object({ pdfId: z.string(), projectId: z.string() })
const access = z.object({
    resourceType: z.enum(["workspace", "entity"]),
    resourceId: z.string(),
})
const comment = z.object({
    projectId: z.string(),
    mapCommentId: z.string(),
    commentId: z.string(),
    mentionedUserIds: z.array(z.string()),
})
export const shareRecipientSchema = z.discriminatedUnion("type", [
    z.object({ type: z.literal("user"), userId: z.string() }),
    z.object({ type: z.literal("email"), email: z.string() }),
    z.object({ type: z.literal("phone"), phoneNumber: z.string() }),
])
export type ShareRecipient = z.infer<typeof shareRecipientSchema>

export const eventRegistry = {
    [EventKind.LayerCreated]: layer,
    [EventKind.LayerDeleted]: layer,
    [EventKind.LayerProcessed]: layer,
    [EventKind.LayerProcessingFailed]: layer,
    [EventKind.LayerArchived]: layer,
    [EventKind.LayerUnarchived]: layer,
    [EventKind.EntityCreated]: entity,
    [EventKind.EntityDeleted]: entity,
    [EventKind.EntityArchived]: entity,
    [EventKind.EntityUnarchived]: entity,
    [EventKind.StreamStarted]: stream,
    [EventKind.StreamEnded]: stream,
    [EventKind.LocationSharingStarted]: stream,
    [EventKind.LocationSharingEnded]: stream,
    [EventKind.PdfGenerated]: pdf,
    [EventKind.PdfFailed]: pdf,
    [EventKind.AccessRequested]: access,
    [EventKind.AccessGranted]: access.extend({ targetUserId: z.string(), permission: z.number() }),
    [EventKind.AccessRemoved]: access.extend({ targetUserId: z.string() }),
    [EventKind.StreamShared]: stream.extend({ recipient: shareRecipientSchema }),
    [EventKind.MissionGroupStreamShared]: z.object({
        layerId: z.string(),
        missionGroupIds: z.array(z.string()),
        recipient: shareRecipientSchema,
    }),
    [EventKind.MissionGroupDashboardShared]: z.object({
        missionGroupIds: z.array(z.string()),
        recipient: shareRecipientSchema,
    }),
    [EventKind.CommentCreated]: comment,
    [EventKind.CommentDeleted]: comment,
} satisfies { [K in EventKind]: z.ZodObject }

export const eventPayloads = eventRegistry

export type LayerEntityType = z.infer<(typeof layer.shape)["layerType"]>
export type EntityType = z.infer<(typeof entity.shape)["entityType"]>
export type AccessResourceType = z.infer<(typeof access.shape)["resourceType"]>
export const layerEntityTypeSchema = layer.shape.layerType
export const accessResourceTypeSchema = access.shape.resourceType

export type EventPayload<K extends EventKind> = z.infer<(typeof eventRegistry)[K]>

type WithMeta<K extends EventKind, Data> = {
    eventKind: K
    region: SupportedRegions
    orgId: string
    actorId: string
    data: Data
}

export type Event<K extends EventKind = EventKind> = {
    [Kind in K]: WithMeta<Kind, EventPayload<Kind>>
}[K]

const meta = { region: z.enum(SupportedRegions), orgId: z.string(), actorId: z.string() }

export const eventSchema = z.discriminatedUnion(
    "eventKind",
    Object.values(EventKind).map((kind) =>
        z.object({ eventKind: z.literal(kind), ...meta, data: eventRegistry[kind] })
    ) as unknown as [z.ZodObject, ...z.ZodObject[]]
) as unknown as z.ZodType<Event>
